import { CollectionPreferencesProps } from '@amzn/awsui-components-console';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/**
 * Table preferences and their localStorage persistence, shared by every table.
 * Per-browser by choice: saved searches moved to the backend UserPreference API,
 * and these are a candidate to follow.
 */

/**
 * How every table starts, app-wide. `wrapLines` is ABSENT, not false: that is
 * already Cloudscape's default, so an absent member lets the Table decide.
 */
const DISPLAY_DEFAULTS = {
  stripedRows: true,
  contentDensity: 'compact',
} as const;

/** Sizes offered by a paginated table unless it declares its own. */
export const DEFAULT_PAGE_SIZE_OPTIONS: readonly number[] = [25, 50, 75];

/**
 * What a table declares to get preferences. Built per-view where columns vary —
 * the search table builds one per mode, whose column ids only partly overlap.
 */
export interface TablePreferencesConfig {
  /** Full localStorage key. Given, not derived: changing one discards saved preferences. */
  readonly storageKey: string;
  /**
   * The preferences list, in default column order. Mirror the table's column
   * definitions: this order is what an unsaved or unmentioned column falls back
   * to.
   */
  readonly columnOptions: readonly CollectionPreferencesProps.ContentDisplayOption[];
  /**
   * Offered page sizes, and the allowlist for values read back from storage.
   * OMIT for a table with no pagination — no page size is then offered,
   * persisted, or returned.
   */
  readonly pageSizeOptions?: readonly number[];
  /** Page size before any choice, and the fallback for an unoffered stored value. */
  readonly defaultPageSize?: number;
  /**
   * Labels a page size. Defaults to a localized "N rows"; search says "N results".
   */
  readonly pageSizeLabel?: (t: TranslateFn, pageSize: number) => string;
  /**
   * Columns hidden until the user turns them on. Still listed, and returning to
   * their declared position when enabled — a starting visibility, not a reorder.
   */
  readonly defaultHiddenColumnIds?: readonly string[];
  /**
   * A column forced LAST however the user reorders. Pair with `alwaysVisible` to
   * also stop it being hidden.
   */
  readonly pinnedLastColumnId?: string;
}

/**
 * What a table persists and applies — Cloudscape's `Preferences`, narrowed.
 * `pageSize` is absent exactly when the config offers no sizes.
 */
export interface TablePreferencesValue {
  readonly pageSize?: number;
  readonly wrapLines?: boolean;
  readonly stripedRows?: boolean;
  readonly contentDensity?: ContentDensity;
  readonly contentDisplay: readonly CollectionPreferencesProps.ContentDisplayColumn[];
}

/** Cloudscape's two row densities. */
export type ContentDensity = 'comfortable' | 'compact';

/**
 * Screen-reader copy for column reordering. Out of the JSX so the builders can be
 * asserted directly — Cloudscape only invokes them mid-drag.
 */
export const columnDisplayDndLabels = (t: TranslateFn) =>
  ({
    liveAnnouncementDndStarted: (position: number, total: number) =>
      t('table-prefs-dnd-started', { position, total }),
    liveAnnouncementDndItemReordered: (
      initialPosition: number,
      currentPosition: number,
    ) =>
      t('table-prefs-dnd-reordered', {
        initial: initialPosition,
        current: currentPosition,
      }),
    liveAnnouncementDndItemCommitted: (
      initialPosition: number,
      finalPosition: number,
    ) =>
      t('table-prefs-dnd-committed', {
        initial: initialPosition,
        final: finalPosition,
      }),
    liveAnnouncementDndDiscarded: t('table-prefs-dnd-discarded'),
    dragHandleAriaLabel: t('table-prefs-drag-handle-aria'),
    dragHandleAriaDescription: t('table-prefs-drag-handle-desc'),
  }) as const;

/**
 * The page size where a definite number is required — a table doing its own
 * pagination arithmetic. `fallback` is reached only for an unpaginated config.
 */
export function pageSizeOf(
  preferences: { readonly pageSize?: number },
  fallback: number,
): number {
  return preferences.pageSize ?? fallback;
}

/** The size a paginated config falls back to; `undefined` when unpaginated. */
function fallbackPageSize(config: TablePreferencesConfig): number | undefined {
  if (config.pageSizeOptions === undefined) return undefined;
  return config.defaultPageSize ?? config.pageSizeOptions[0];
}

function isOfferedPageSize(
  value: unknown,
  config: TablePreferencesConfig,
): value is number {
  return (
    typeof value === 'number' &&
    config.pageSizeOptions !== undefined &&
    config.pageSizeOptions.includes(value)
  );
}

/** Whether a column starts hidden. `alwaysVisible` wins over `defaultHidden`. */
function startsHidden(id: string, config: TablePreferencesConfig): boolean {
  return config.defaultHiddenColumnIds?.includes(id) === true;
}

/**
 * Every column in the config's declared order, at its default visibility — all
 * visible unless `defaultHiddenColumnIds` says otherwise.
 */
function defaultContentDisplay(
  config: TablePreferencesConfig,
): CollectionPreferencesProps.ContentDisplayColumn[] {
  return config.columnOptions.map((option) => ({
    id: option.id,
    visible: option.alwaysVisible === true || !startsHidden(option.id, config),
  }));
}

/** The defaults, and the fallback for anything unreadable in storage. */
export function defaultTablePreferences(
  config: TablePreferencesConfig,
): TablePreferencesValue {
  return {
    pageSize: fallbackPageSize(config),
    ...defaultDisplay(),
    contentDisplay: defaultContentDisplay(config),
  };
}

/** The shared display defaults as a value; see `DISPLAY_DEFAULTS`. */
function defaultDisplay(): {
  wrapLines?: boolean;
  stripedRows?: boolean;
  contentDensity?: ContentDensity;
} {
  return { ...DISPLAY_DEFAULTS };
}

/** A boolean from an untrusted source, or the table's default if it has one. */
function readBoolean(
  value: unknown,
  fallback: boolean | undefined,
): boolean | undefined {
  return typeof value === 'boolean' ? value : fallback;
}

/** A density from an untrusted source, or the table's default if it has one. */
function readDensity(
  value: unknown,
  fallback: ContentDensity | undefined,
): ContentDensity | undefined {
  return value === 'comfortable' || value === 'compact' ? value : fallback;
}

/**
 * Reconciles any `contentDisplay` against a config's columns: junk ignored, unknown
 * ids dropped, unmentioned columns at their default, and never zero columns.
 */
export function sanitiseContentDisplay(
  stored: unknown,
  config: TablePreferencesConfig,
): CollectionPreferencesProps.ContentDisplayColumn[] {
  const options = config.columnOptions;
  const pinnedId = config.pinnedLastColumnId;

  // Stored visibility by id, in order. First entry wins for a duplicated id.
  const storedVisibility = new Map<string, boolean>();
  const storedOrder: string[] = [];

  if (Array.isArray(stored)) {
    for (const entry of stored) {
      if (!entry || typeof entry !== 'object') continue;
      const { id, visible } = entry as Record<string, unknown>;
      if (typeof id !== 'string' || typeof visible !== 'boolean') continue;
      if (storedVisibility.has(id)) continue;
      storedVisibility.set(id, visible);
      storedOrder.push(id);
    }
  }

  const knownIds = new Set(options.map((option) => option.id));
  const alwaysVisibleIds = new Set(
    options.filter((option) => option.alwaysVisible).map((option) => option.id),
  );
  const isPinned = (id: string): boolean => id === pinnedId;

  const orderedIds = [
    // Stored order, unknown ids dropped …
    ...storedOrder.filter((id) => knownIds.has(id) && !isPinned(id)),
    // … then any column the stored value did not mention, in declared order.
    ...options
      .map((option) => option.id)
      .filter((id) => !storedVisibility.has(id) && !isPinned(id)),
    // The pinned column always ends up here. See `pinnedLastColumnId`.
    ...(pinnedId !== undefined && knownIds.has(pinnedId) ? [pinnedId] : []),
  ];

  const items = orderedIds.map((id) => ({
    id,
    visible: alwaysVisibleIds.has(id)
      ? true
      : // A column the stored value never mentioned takes its DEFAULT visibility,
        // so a newly added default-hidden column does not appear for existing users.
        (storedVisibility.get(id) ?? !startsHidden(id, config)),
  }));

  // Compared against the defaults so an all-hidden config cannot loop back empty.
  if (!items.some((item) => item.visible)) {
    const defaults = defaultContentDisplay(config);
    return defaults.some((item) => item.visible) ? defaults : items;
  }

  return items;
}

/** Normalises a confirm payload, validated exactly as a stored value is. */
export function normaliseTablePreferences(
  preferences: CollectionPreferencesProps.Preferences,
  config: TablePreferencesConfig,
): TablePreferencesValue {
  const display = defaultDisplay();
  return {
    pageSize: isOfferedPageSize(preferences.pageSize, config)
      ? preferences.pageSize
      : fallbackPageSize(config),
    wrapLines: readBoolean(preferences.wrapLines, display.wrapLines),
    stripedRows: readBoolean(preferences.stripedRows, display.stripedRows),
    contentDensity: readDensity(
      preferences.contentDensity,
      display.contentDensity,
    ),
    contentDisplay: sanitiseContentDisplay(preferences.contentDisplay, config),
  };
}

/** Reads persisted preferences. Never throws: bad storage must not break the page. */
export function loadTablePreferences(
  config: TablePreferencesConfig,
): TablePreferencesValue {
  try {
    const raw = localStorage.getItem(config.storageKey);
    if (!raw) return defaultTablePreferences(config);

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return defaultTablePreferences(config);
    }

    const record = parsed as Record<string, unknown>;
    const display = defaultDisplay();
    return {
      pageSize: isOfferedPageSize(record.pageSize, config)
        ? record.pageSize
        : fallbackPageSize(config),
      wrapLines: readBoolean(record.wrapLines, display.wrapLines),
      stripedRows: readBoolean(record.stripedRows, display.stripedRows),
      contentDensity: readDensity(
        record.contentDensity,
        display.contentDensity,
      ),
      contentDisplay: sanitiseContentDisplay(record.contentDisplay, config),
    };
  } catch {
    return defaultTablePreferences(config);
  }
}

/** Persists preferences. A failed write is swallowed; they still apply this session. */
export function saveTablePreferences(
  config: TablePreferencesConfig,
  preferences: TablePreferencesValue,
): void {
  try {
    localStorage.setItem(config.storageKey, JSON.stringify(preferences));
  } catch {
    // Intentionally ignored — see the doc comment.
  }
}
